import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Modal, useToast } from '../primitives';
import {
  CHAIN_LABEL,
  CHAIN_TARGETS,
  buildTarget,
  chainPath,
  previousZp,
  readChainRef,
  smlouvaOf,
  stashTransfer,
  takeTransfer,
  type ChainDocType,
  type ChainRef,
  type TransferOptions,
} from '../../lib/docChain';

/**
 * Stav řetězení v jednom formuláři.
 *
 * - `zdroj` — odkaz na doklad, ze kterého tenhle vznikl (ukládá se do `data_json.zdroj`),
 * - `savedId` — id z prvního uložení nového dokladu v této relaci; formulář
 *   ukládá s `id: editId ?? chain.savedId`, aby „Uložit" → „Převést na…"
 *   nevyrobilo v archivu duplikát.
 *
 * Formulář volá `chain.apply(d)` ve svém `applySnapshot` a přidá
 * `zdroj: chain.zdroj` do `docData`.
 */
export function useDocChain() {
  const [zdroj, setZdroj] = useState<ChainRef | null>(null);
  const [savedId, setSavedId] = useState<number | null>(null);
  return {
    zdroj,
    apply(d: Record<string, unknown>) {
      if ('zdroj' in d) setZdroj(readChainRef(d.zdroj));
    },
    savedId,
    markSaved(id: number | null | undefined) {
      if (id != null) setSavedId(id);
    },
  };
}

/**
 * Převzetí dat z jiného dokladu (tlačítko „Převést na…"). Jen u nového
 * formuláře (bez `?id`); data se vyzvednou jednou a ze sessionStorage smažou.
 */
export function useDocTransferIn(
  docType: ChainDocType,
  applySnapshot: (d: Record<string, unknown>) => void,
  editId: number | null,
) {
  const toast = useToast();
  const applyRef = useRef(applySnapshot);
  applyRef.current = applySnapshot;
  useEffect(() => {
    if (editId != null) return;
    const d = takeTransfer(docType);
    if (!d) return;
    applyRef.current(d);
    const z = readChainRef(d.zdroj);
    toast.success(
      z ? `Předvyplněno z dokladu „${z.title}". Zkontrolujte a uložte.` : 'Předvyplněno. Zkontrolujte a uložte.',
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docType, editId]);
}

/** Informační pruh nad formulářem: z čeho doklad vznikl (+ volitelné akce). */
export function DocChainBar({ zdroj, children }: { zdroj: ChainRef | null; children?: ReactNode }) {
  if (!zdroj && !children) return null;
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 10,
        margin: '0 0 12px',
        padding: '8px 14px',
        borderRadius: 8,
        border: '1px solid var(--sd-border)',
        background: 'var(--sd-surface-2)',
        fontSize: 13,
        color: 'var(--sd-text-dim)',
      }}
    >
      {zdroj && (
        <span style={{ flex: '1 1 240px' }}>
          ↳ Navazuje na{' '}
          <Link to={`${chainPath(zdroj.doc_type)}?id=${zdroj.id}`} style={{ color: 'var(--sd-yellow-text)' }}>
            {zdroj.title || CHAIN_LABEL[zdroj.doc_type]}
          </Link>
          {zdroj.smlouva?.cislo ? <> · smlouva/objednávka č. {zdroj.smlouva.cislo}</> : null}
        </span>
      )}
      {children}
    </div>
  );
}

export interface DocTransferButtonProps {
  docType: ChainDocType;
  /** Aktuální snímek dat formuláře (`docData`). */
  getData: () => Record<string, unknown>;
  /** Uloží doklad do archivu a vrátí jeho id (null = neuloženo, chybu ukáže sám). */
  save: () => Promise<number | null>;
  /** Titulek dokladu (ukládá se do odkazu v navazujícím dokladu). */
  title: string;
  zdroj: ChainRef | null;
}

/**
 * „⇢ Převést na…" — uloží doklad a otevře navazující doklad předvyplněný
 * z tohoto. U převodu na soupis z nabídky/objednávky se zeptá, jestli jde
 * o fakturaci celou najednou, nebo postupnou (množství se vyplní skutečně provedená).
 */
export function DocTransferButton({ docType, getData, save, title, zdroj }: DocTransferButtonProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [askSoupis, setAskSoupis] = useState(false);
  const [busy, setBusy] = useState(false);
  const targets = CHAIN_TARGETS[docType];
  if (!targets.length) return null;

  async function go(target: ChainDocType, postupne = false) {
    setBusy(true);
    try {
      const id = await save();
      if (id == null) return;
      const data = getData();
      const ref: ChainRef = {
        id,
        doc_type: docType,
        title,
        koren: zdroj?.koren ?? id,
        smlouva: smlouvaOf(docType, data, zdroj),
      };
      const opt: TransferOptions = { postupne, smlouva: ref.smlouva };
      if (target === 'zjistovaci') {
        const prev = await previousZp(ref.koren, String(data.zakazka ?? ''), null);
        opt.prevZp = prev.sum;
        opt.zpCislo = String(prev.count + 1);
      }
      const snap = buildTarget(docType, data, target, opt);
      snap.zdroj = ref;
      stashTransfer(target, snap);
      setOpen(false);
      setAskSoupis(false);
      navigate(chainPath(target), { state: { fresh: Date.now() } });
    } catch (e) {
      toast.error(e instanceof Error && e.message ? `Převod se nepovedl: ${e.message}` : 'Převod se nepovedl.');
    } finally {
      setBusy(false);
    }
  }

  function pick(target: ChainDocType) {
    if (target === 'soupis' && (docType === 'nabidka' || docType === 'objednavka')) {
      setAskSoupis(true);
      return;
    }
    void go(target);
  }

  return (
    <>
      <Button variant="secondary" leading="⇢" onClick={() => setOpen(true)}>
        Převést na…
      </Button>
      {open && (
        <Modal
          open
          onClose={() => !busy && (setOpen(false), setAskSoupis(false))}
          title={askSoupis ? 'Soupis prací — jak fakturovat?' : 'Převést na navazující doklad'}
        >
          {!askSoupis ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <p style={{ margin: '0 0 6px', fontSize: 13, color: 'var(--sd-text-dim)' }}>
                Doklad se nejdřív uloží do archivu. Nový doklad se otevře předvyplněný a zůstane
                s tímto propojený.
              </p>
              {targets.map((t) => (
                <Button key={t} variant="secondary" disabled={busy} onClick={() => pick(t)}>
                  {CHAIN_LABEL[t]}
                </Button>
              ))}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Button variant="primary" disabled={busy} onClick={() => void go('soupis', false)}>
                Celé najednou — převzít množství
              </Button>
              <Button variant="secondary" disabled={busy} onClick={() => void go('soupis', true)}>
                Postupně — prázdná množství, vyplním skutečně provedené
              </Button>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--sd-text-dim)' }}>
                Při postupné fakturaci lze z jedné nabídky/objednávky založit soupis každý měsíc.
                Zjišťovací protokol pak sám dopočítá „dosud provedeno" z předchozích ZP.
              </p>
            </div>
          )}
          {busy && <div style={{ marginTop: 10, fontSize: 13 }}>Ukládám a připravuji…</div>}
        </Modal>
      )}
    </>
  );
}
